// lib/utils/tracking.ts — pure tracking utilities (no React)

const SESSION_KEY = 'ms_session_id';
const VISITOR_KEY = 'ms_visitor_id';

const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/portfolio/api/v1';

// ── Session & visitor ID persistence ─────────────────────────────────────────

/**
 * Returns the sessionId for the current browser tab.
 * Stored in sessionStorage so it resets when the tab closes.
 */
export function getOrCreateSessionId(): string {
    if (typeof sessionStorage === 'undefined') return crypto.randomUUID();
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
        sid = crypto.randomUUID();
        sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
}

/** Returns the visitorId persisted from a previous visit, or null for new visitors. */
export function getStoredVisitorId(): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(VISITOR_KEY);
}

/** Persists the visitorId returned by the backend for future visits. */
export function saveVisitorId(id: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(VISITOR_KEY, id);
}

/**
 * Returns the persisted visitorId, creating + saving one if absent. Used by
 * interactions (e.g. blog likes) that need a stable identity even when they
 * fire before the tracking POST has returned a server-issued id.
 */
export function getOrCreateVisitorId(): string {
    const existing = getStoredVisitorId();
    if (existing) return existing;
    const id = crypto.randomUUID();
    saveVisitorId(id);
    return id;
}

// ── Device / environment info ─────────────────────────────────────────────────

export interface DeviceInfo {
    screen: { width: number; height: number; colorDepth: number };
    viewport: { width: number; height: number };
    language: string;
    languages: string[];
    timezone: string;
    darkMode: boolean;
    touchSupport: boolean;
    cookiesEnabled: boolean;
    connection?: {
        type?: string;
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
    };
}

export function collectDeviceInfo(): DeviceInfo {
    const nav  = navigator as any;
    const conn = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;

    return {
        screen: {
            width:      window.screen.width,
            height:     window.screen.height,
            colorDepth: window.screen.colorDepth,
        },
        viewport: {
            width:  window.innerWidth,
            height: window.innerHeight,
        },
        language:       navigator.language,
        languages:      [...(navigator.languages ?? [navigator.language])],
        timezone:       Intl.DateTimeFormat().resolvedOptions().timeZone,
        darkMode:       window.matchMedia('(prefers-color-scheme: dark)').matches,
        touchSupport:   'ontouchstart' in window || navigator.maxTouchPoints > 0,
        cookiesEnabled: navigator.cookieEnabled,
        connection: conn
            ? {
                  type:          conn.type,
                  effectiveType: conn.effectiveType,
                  downlink:      conn.downlink,
                  rtt:           conn.rtt,
              }
            : undefined,
    };
}

// ── UTM parameters ────────────────────────────────────────────────────────────

export function extractUtmParams(): Record<string, string> | undefined {
    if (typeof window === 'undefined') return undefined;
    const params  = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const result: Record<string, string> = {};
    for (const key of utmKeys) {
        const val = params.get(key);
        if (val) result[key] = val;
    }
    return Object.keys(result).length ? result : undefined;
}

// ── Performance / scroll ──────────────────────────────────────────────────────

/** Page load time in ms via Navigation Timing API. Returns 0 if not available. */
export function getPageLoadTime(): number {
    if (typeof performance === 'undefined') return 0;
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const nav     = entries[0];
    return nav ? Math.round(nav.loadEventEnd - nav.startTime) : 0;
}

/** Current scroll depth as a percentage (0–100) of full page height. */
export function getScrollDepth(): number {
    if (typeof window === 'undefined') return 0;
    const scrolled = window.scrollY + window.innerHeight;
    const total    = document.documentElement.scrollHeight;
    return total > 0 ? Math.min(100, Math.floor((scrolled / total) * 100)) : 0;
}

// ── Interaction events ──────────────────────────────────────────────────────────

export type EventCategory = 'engagement' | 'conversion' | 'navigation' | 'media' | 'other';

export interface TrackEventInput {
    type:       string;        // e.g. 'cv_download', 'contact_submit', 'project_click'
    category?:  EventCategory;
    target?:    string;        // what was acted on (slug, href, label)
    page?:      string;        // defaults to current path
    value?:     number;        // optional numeric (scroll %, watch seconds)
    metadata?:  Record<string, unknown>;
}

/**
 * POST /tracking/event — fire-and-forget granular interaction event.
 * Uses sendBeacon so it survives navigation/unload; falls back to keepalive fetch.
 * No-ops on the server and before a visitorId exists.
 */
export function trackEvent(input: TrackEventInput): void {
    if (typeof window === 'undefined') return;
    try {
        const body = JSON.stringify({
            visitorId: getOrCreateVisitorId(),
            sessionId: getOrCreateSessionId(),
            type:      input.type,
            category:  input.category,
            target:    input.target,
            page:      input.page ?? window.location.pathname,
            value:     input.value,
            metadata:  input.metadata,
        });
        const url = `${API_BASE_URL}/tracking/event`;
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        } else {
            fetch(url, {
                method:    'POST',
                headers:   { 'Content-Type': 'application/json' },
                body,
                keepalive: true,
            });
        }
    } catch {
        // fire-and-forget — never block UX
    }
}

// ── Exit beacon ───────────────────────────────────────────────────────────────

/**
 * PATCH /tracking/pageview/:id with final metrics.
 * Uses fetch({ keepalive: true }) so it completes even during page unload.
 */
export function patchPageView(
    pageViewId: string,
    data: { timeOnPage: number; scrollDepth: number; isExit: boolean }
): void {
    if (typeof window === 'undefined' || !pageViewId) return;
    try {
        fetch(`${API_BASE_URL}/tracking/pageview/${pageViewId}`, {
            method:    'PATCH',
            headers:   { 'Content-Type': 'application/json' },
            body:      JSON.stringify(data),
            keepalive: true,
        });
    } catch {
        // fire-and-forget — never block UX
    }
}
